import { useRef, useState } from 'react';
import { Button } from 'react-bootstrap';
import { Trans, useTranslation } from 'react-i18next';
import { useToast } from 'context/ToastContext';
import spinnerTitle from 'components/others/spinnerTitle';
import { parseImportCsv } from './model/parseImportCsv';
import type { ImportProductItem, ImportVendorLookup } from './model/types';
import { getCurrencyI18nParams } from 'utils/helpers/currency/currencyHelpers';

interface ImportCsvInfoProps {
    vendors: ImportVendorLookup[];
    itemsCount: number;
    onParsed: (items: ImportProductItem[]) => void;
    pricesInUsd: boolean;
    onPricesInUsdChange: (next: boolean) => void;
    usdRate: number;
    isImporting: boolean;
    onImport: () => void;
}

const ImportCsvInfo = ({
    vendors,
    itemsCount,
    onParsed,
    pricesInUsd,
    onPricesInUsdChange,
    usdRate,
    isImporting,
    onImport,
}: ImportCsvInfoProps) => {
    const { t } = useTranslation(['Products', 'common']);
    const { showToast } = useToast();
    const refInputFile = useRef<HTMLInputElement>(null);
    const [isReading, setIsReading] = useState(false);

    const handleOpenClick = () => {
        if (isReading) return;
        refInputFile.current?.click();
    };

    const handleChangeFile = () => {
        const file = refInputFile.current?.files?.[0];
        if (!file) return;
        setIsReading(true);

        const reader = new FileReader();

        reader.onload = () => {
            const content = typeof reader.result === 'string' ? reader.result : '';
            const { items, skipped } = parseImportCsv(content, vendors);

            onParsed(items);

            if (items.length) {
                showToast(
                    skipped
                        ? t('importCsv.messLoadedWithSkipped', { count: items.length, skipped })
                        : t('importCsv.messLoaded', { count: items.length }),
                    'success',
                );
            } else {
                showToast(t('importCsv.messEmpty'), 'warning');
            }

            if (refInputFile.current) {
                refInputFile.current.value = '';
            }
            setIsReading(false);
        };

        reader.onerror = () => {
            showToast(t('importCsv.messReadError'), 'danger');
            if (refInputFile.current) {
                refInputFile.current.value = '';
            }
            setIsReading(false);
        };

        reader.readAsText(file, 'UTF-8');
    };

    return (
        <div className="alert alert-info mb-3" role="alert">
            <div className="d-flex align-items-start gap-2 mb-2">
                <i className="bi bi-filetype-csv fs-5 flex-shrink-0"></i>
                <div className="fw-semibold">{t('importCsv.formatTitle')}</div>
            </div>
            <p className="mb-2">{t('importCsv.formatDesc')}</p>
            <code className="d-block p-2 rounded bg-body border small mb-3">
                {t('importCsv.formatExample', getCurrencyI18nParams())}
            </code>
            {usdRate !== 1 && (
            <div className="form-check form-switch mb-3">
                <input
                    className="form-check-input"
                    type="checkbox"
                    role="switch"
                    id="import-csv-price-usd-switch"
                    checked={pricesInUsd}
                    onChange={(e) => onPricesInUsdChange(Boolean(e.target.checked))}
                />
                <label className="form-check-label" htmlFor="import-csv-price-usd-switch">
                    {t('importCsv.pricesInUsdSwitch', getCurrencyI18nParams())}
                </label>
                {!pricesInUsd && (
                    <div className="small text-muted">
                        {t('importCsv.pricesConvertHint', {
                            ...getCurrencyI18nParams(),
                            rate: Number(usdRate || 0).toFixed(2),
                        })}
                    </div>
                )}
            </div>
            )}
            <div className="d-flex flex-wrap gap-2">
                <Button type="button" variant="primary" size="sm" onClick={handleOpenClick} disabled={isReading}>
                    <i className="bi bi-folder2-open me-1"></i>
                    {t('importCsv.open')}
                </Button>
                {itemsCount > 0 && (
                    <Button type="button" variant="success" size="sm" disabled={isReading || isImporting} onClick={onImport}>
                        {spinnerTitle(
                            <>
                                <i className="bi bi-box-arrow-in-down me-1"></i>
                                {t('importCsv.import')}
                            </>,
                            isImporting,
                            t('importCsv.importing'),
                        )}
                    </Button>
                )}
            </div>
            <div className="small text-muted mt-2">
                <Trans i18nKey="importCsv.editHint" ns="Products" components={{ strong: <strong /> }} />
            </div>
            <input
                ref={refInputFile}
                type="file"
                accept=".csv,text/csv"
                className="d-none"
                onChange={handleChangeFile}
            />
        </div>
    );
};

export default ImportCsvInfo;
